import { useEffect } from 'react';
import { logger } from '@/utils/logger';
import { createAuthenticatedWebSocket, isAuthError, isFatalError, handleWebSocketAuthError } from '@/shared/utils/websocket';

interface WebSocketMessage {
  type: 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
  data: {
    time_log_id: string;
    staff_id: string;
    staff_name: string;
    staff_email: string;
    clocked_in_at: string;
    clocked_out_at?: string;
    status: string;
    is_on_break: boolean;
    duration_hours?: number;
    total_break_minutes?: number;
    net_work_hours?: number;
    break_start?: string;
  };
}

interface UseTimeTrackingWebSocketOptions {
  onMessage: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

// Singleton WebSocket manager to prevent multiple connections during React StrictMode
class TimeTrackingWebSocketManager {
  private static instance: TimeTrackingWebSocketManager | null = null;
  private ws: WebSocket | null = null;
  private reconnectTimeout: NodeJS.Timeout | undefined;
  private shouldReconnect: boolean = true;
  private subscribers: Set<(message: WebSocketMessage) => void> = new Set();
  private connectListeners: Set<() => void> = new Set();
  private disconnectListeners: Set<() => void> = new Set();
  private errorListeners: Set<(error: Event) => void> = new Set();
  private refCount: number = 0;
  private disconnectTimeout: NodeJS.Timeout | undefined;

  private constructor() {}

  static getInstance(): TimeTrackingWebSocketManager {
    if (!TimeTrackingWebSocketManager.instance) {
      TimeTrackingWebSocketManager.instance = new TimeTrackingWebSocketManager();
    }
    return TimeTrackingWebSocketManager.instance;
  }

  addRef(): void {
    this.refCount++;

    // Cancel any pending disconnect when a new component mounts
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout);
      this.disconnectTimeout = undefined;
    }

    if (this.refCount === 1 && (!this.ws || this.ws.readyState === WebSocket.CLOSED)) {
      this.shouldReconnect = true;
      this.connect();
    }
  }

  removeRef(): void {
    this.refCount--;
    if (this.refCount === 0) {
      // Delay disconnect to handle React StrictMode double-mounting
      this.disconnectTimeout = setTimeout(() => {
        this.disconnect();
        this.disconnectTimeout = undefined;
      }, 100);
    }
  }

  private connect(): void {
    if (!this.shouldReconnect) {
      return;
    }

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      logger.warn('No access token found, cannot connect to WebSocket');
      return;
    }

    if (this.ws) {
      const oldWs = this.ws;
      this.ws = null;
      oldWs.onopen = null;
      oldWs.onmessage = null;
      oldWs.onerror = null;
      oldWs.onclose = null;
      oldWs.close();
    }

    try {
      // Create authenticated WebSocket with automatic auth error handling
      const ws = createAuthenticatedWebSocket('/time-tracking');
      if (!ws) {
        logger.error('Failed to create WebSocket connection');
        return;
      }

      this.ws = ws;

      ws.onopen = () => {
        logger.debug('WebSocket connected');
        this.connectListeners.forEach(listener => {
          try {
            listener();
          } catch (err) {
            logger.error('Error in connect listener:', err);
          }
        });
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.subscribers.forEach(callback => {
            try {
              callback(message);
            } catch (err) {
              logger.error('Error in WebSocket message handler:', err);
            }
          });
        } catch (err) {
          logger.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        logger.error('WebSocket error:', error);
        this.errorListeners.forEach(listener => {
          try {
            listener(error);
          } catch (err) {
            logger.error('Error in error listener:', err);
          }
        });
      };

      ws.onclose = (event) => {
        logger.debug('WebSocket disconnected', { code: event.code });
        console.log('[Time Tracking WebSocket] Connection closed with code:', event.code, 'reason:', event.reason);

        // Check if close was due to auth error
        if (isAuthError(event.code)) {
          logger.warn('WebSocket closed due to authentication error');
          console.log('[Time Tracking WebSocket] Detected auth error, calling handleWebSocketAuthError()');
          handleWebSocketAuthError();
          return; // Don't attempt reconnection
        }

        // Check if this is a fatal error (404, protocol error, etc.)
        if (isFatalError(event.code)) {
          logger.warn('WebSocket closed with fatal error code:', event.code);
          console.log('[Time Tracking WebSocket] Fatal error detected, stopping reconnection attempts');
          this.shouldReconnect = false; // Stop trying to reconnect
        }

        this.disconnectListeners.forEach(listener => {
          try {
            listener();
          } catch (err) {
            logger.error('Error in disconnect listener:', err);
          }
        });

        if (this.ws === ws) {
          this.ws = null;
        }

        if (this.shouldReconnect) {
          this.reconnectTimeout = setTimeout(() => {
            logger.debug('Attempting to reconnect WebSocket...');
            this.connect();
          }, 3000);
        }
      };
    } catch (err) {
      logger.error('Failed to create WebSocket connection:', err);
    }
  }

  private disconnect(): void {
    this.shouldReconnect = false;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  subscribe(
    onMessage: (message: WebSocketMessage) => void,
    onConnect?: () => void,
    onDisconnect?: () => void,
    onError?: (error: Event) => void
  ): () => void {
    this.subscribers.add(onMessage);
    if (onConnect) this.connectListeners.add(onConnect);
    if (onDisconnect) this.disconnectListeners.add(onDisconnect);
    if (onError) this.errorListeners.add(onError);

    return () => {
      this.subscribers.delete(onMessage);
      if (onConnect) this.connectListeners.delete(onConnect);
      if (onDisconnect) this.disconnectListeners.delete(onDisconnect);
      if (onError) this.errorListeners.delete(onError);
    };
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export const useTimeTrackingWebSocket = ({
  onMessage,
  onConnect,
  onDisconnect,
  onError,
}: UseTimeTrackingWebSocketOptions) => {
  useEffect(() => {
    const manager = TimeTrackingWebSocketManager.getInstance();
    manager.addRef();

    const unsubscribe = manager.subscribe(onMessage, onConnect, onDisconnect, onError);

    return () => {
      unsubscribe();
      manager.removeRef();
    };
  }, [onMessage, onConnect, onDisconnect, onError]);

  return {
    disconnect: () => TimeTrackingWebSocketManager.getInstance().removeRef(),
    reconnect: () => {
      const manager = TimeTrackingWebSocketManager.getInstance();
      manager.removeRef();
      manager.addRef();
    },
    isConnected: TimeTrackingWebSocketManager.getInstance().isConnected(),
  };
};
